import { Alert, DialogContent, Typography } from 'opensolar-ui'
import type { Identifier } from 'ra-core'
import { useCallback, useRef, useState } from 'react'
import { useDataProvider, useNotify, useRefresh, useTranslate, useUnselectAll } from 'react-admin'
import { Form } from 'react-final-form'

import { BULK_EDIT_FORM_ID } from './constants'
import { BulkEditBulkConfirmPanel, BulkEditFormActions } from './BulkEditDialogStates'
import { buildBulkEditVisiblePatch } from './helpers'
import {
  listBulkEditEmitSaveFailed,
  listBulkEditEmitSaveSuccess,
  listBulkEditPerformUpdate,
} from './listBulkEditApply'
import type { BulkEditResourceDefinition } from './types'

type Props = {
  definition: BulkEditResourceDefinition
  resource: string
  effectiveIds: Identifier[]
  initialFormValues: Record<string, unknown>
  formFieldVisibility: string[] | null
  patchVisibilityKeys: string[] | undefined
  onClose: () => void
}

export function BulkEditDialogForm({
  definition: def,
  resource,
  effectiveIds,
  initialFormValues,
  formFieldVisibility,
  patchVisibilityKeys,
  onClose,
}: Props) {
  const translate = useTranslate()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const unselectAll = useUnselectAll()

  const [step, setStep] = useState<'form' | 'confirm'>('form')
  const pendingPatchRef = useRef<Record<string, unknown> | null>(null)

  const [applying, setApplying] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [formValidationError, setFormValidationError] = useState<string | null>(null)

  const executeApply = useCallback(
    async (patch: Record<string, unknown>) => {
      setApplying(true)
      setSaveFailed(false)
      try {
        await listBulkEditPerformUpdate(dataProvider, resource, effectiveIds, patch)
        listBulkEditEmitSaveSuccess({
          resource,
          recordCount: effectiveIds.length,
          autoAccept: false,
          notify,
          translate,
          refresh,
          unselectAll,
        })
        onClose()
      } catch {
        listBulkEditEmitSaveFailed({ resource, recordCount: effectiveIds.length, autoAccept: false })
        setSaveFailed(true)
      } finally {
        setApplying(false)
      }
    },
    [dataProvider, effectiveIds, notify, onClose, refresh, resource, translate, unselectAll]
  )

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setFormValidationError(null)
      setSaveFailed(false)
      const patch = buildBulkEditVisiblePatch(def, values, patchVisibilityKeys)
      if (Object.keys(patch).length === 0) {
        setFormValidationError(translate('Choose at least one field to update.'))
        return
      }
      pendingPatchRef.current = patch
      setStep('confirm')
    },
    [def, patchVisibilityKeys, translate]
  )

  const handleBackFromConfirm = useCallback(() => {
    pendingPatchRef.current = null
    setSaveFailed(false)
    setStep('form')
  }, [])

  const FormFields = def.FormFields

  if (step === 'confirm' && pendingPatchRef.current) {
    return (
      <BulkEditBulkConfirmPanel
        message={translate('Apply these changes to all selected records?')}
        selectedCountText={translate('Selected %{smart_count} record |||| Selected %{smart_count} records', {
          smart_count: effectiveIds.length,
        })}
        onBack={handleBackFromConfirm}
        onApply={() => executeApply(pendingPatchRef.current!)}
        applying={applying}
        translate={translate}
        backButtonKey="ra.action.back"
        saveFailed={saveFailed}
        failedRecordIds={effectiveIds}
        failedRecordCount={effectiveIds.length}
      />
    )
  }

  return (
    <Form initialValues={initialFormValues} onSubmit={handleSubmit}>
      {({ handleSubmit: submitForm, submitting }) => (
        <>
          <DialogContent margin="normal" style={{ marginTop: 20 }}>
            {def.introKey ? (
              <Typography variant="body2" style={{ display: 'block', marginBottom: 16 }}>
                {translate(def.introKey)}
              </Typography>
            ) : null}
            <Typography variant="body2" style={{ display: 'block', marginBottom: 16 }}>
              {translate('Selected %{smart_count} record |||| Selected %{smart_count} records', {
                smart_count: effectiveIds.length,
              })}
            </Typography>
            {formValidationError ? (
              <Alert severity="warning" style={{ marginBottom: 12 }}>
                {formValidationError}
              </Alert>
            ) : null}
            <form id={BULK_EDIT_FORM_ID} onSubmit={submitForm} noValidate>
              <FormFields disabled={applying || submitting} visibleFieldKeys={formFieldVisibility} />
            </form>
          </DialogContent>
          <BulkEditFormActions
            onClose={onClose}
            translate={translate}
            applying={applying}
            submitting={submitting}
          />
        </>
      )}
    </Form>
  )
}
